import { useEffect, useMemo, useRef } from 'react';

import {
	type StoreChatItem,
	isStoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { areMessageGroupsEqual } from './are-message-groups-equal';

/**
 * Groups a flat list of chat items into "message groups". A new group starts at
 * each HUMAN message; a leading non-HUMAN message (assistant greeting, system
 * message, error, etc.) also starts the first group.
 *
 * Memoized so downstream consumers (virtualizer, render) are stable across
 * unrelated renders. When stable identity preservation is enabled, the hook
 * reuses equivalent groups from the previous committed render.
 */
export const useMessageGroups = (
	conversationMessages: ReadonlyArray<StoreChatItem>,
	preserveStableGroupIdentity: boolean = false,
): Array<Array<StoreChatItem>> => {
	const previousGroupsRef = useRef<Array<Array<StoreChatItem>>>([]);

	const groups = useMemo(() => {
		const nextGroups: Array<Array<StoreChatItem>> = [];
		for (const msg of conversationMessages) {
			if (isStoreHuman(msg)) {
				nextGroups.push([msg]);
				continue;
			}
			if (nextGroups.length === 0) {
				nextGroups.push([msg]);
			} else {
				nextGroups[nextGroups.length - 1].push(msg);
			}
		}
		return nextGroups;
	}, [conversationMessages]);

	const messageGroups = useMemo(() => {
		if (!preserveStableGroupIdentity) {
			return groups;
		}

		let allGroupsPreserved = groups.length === previousGroupsRef.current.length;
		const nextGroups = groups.map((group, index) => {
			const previousGroup = previousGroupsRef.current[index];
			if (areMessageGroupsEqual(previousGroup, group)) {
				return previousGroup;
			}

			allGroupsPreserved = false;
			return group;
		});

		return allGroupsPreserved ? previousGroupsRef.current : nextGroups;
	}, [groups, preserveStableGroupIdentity]);

	useEffect(() => {
		previousGroupsRef.current = messageGroups;
	}, [messageGroups]);

	return messageGroups;
};
